import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import { toast } from "react-toastify";
import queryString from "query-string";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Multiselect } from "react-widgets";
import { confirmAlert } from "react-confirm-alert";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import ExpandDiv from "../common/ExpandDiv";
import LineGraph from "../common/LineGraph";
import { FormInput, FormRadioButton } from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  getFormsySubmissionsByFormId,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  updateEntity,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function CSSRsScreener() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`,
        true
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
      //only do on Signature, not on save. Here for easy testing purposes
      //writeToContact(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
      writeToContact(formsyDetailsObj);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const writeToContact = (current) => {
    //user may be updating/signing a backdated screener,
    //check to see if a screener with a newer assessment date exists
    //
    //If the date is equalTo, let the current screener trup so that a newer one does not exist
    const newerExists =
      allScreeners.filter(
        (s) =>
          new Date(s.FormResults.results.DateOfAssessment["@value"]) >
          new Date(current.FormResults.results.DateOfAssessment["@value"])
      ).length > 0;

    let risklevel = "";
    if (newerExists === false) {
      switch (current.FormResults.results.RiskLevel["@value"]) {
        case "High":
          risklevel = 186460003;
          break;
        case "Moderate":
          risklevel = 186460002;
          break;
        case "Low":
          risklevel = 186460001;
          break;
        default:
          return null;
      }

      console.log(
        "Risk Current Screener",
        current,
        current.FormResults?.results?.RiskLevel["@value"],
        newerExists,
        risklevel
      );

      const obj = {
        sor_risklevel: {
          type: "optionset",
          value: risklevel,
        },
      };

      const toastId = toast.loading("Updating Contact Risk Level...", {
        type: toast.TYPE.INFO,
      });
      updateEntity("contact", clientid, JSON.stringify(obj), safyInfo)
        .then(() => {
          toast.update(toastId, {
            render: "Risk Level has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Risk Level.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    }

    //add current screener if it has not finished being added to allScreeners yet
    // all.push(current);

    // //do a two date, secondary sort, to get most recent screener
    // //the current screener may be updating a previous data state
    // //so want to bubble the most rectent updated to the top
    // all.sort((a, b) => {
    //   const aCreated = new Date(
    //     a.FormResults.results.DateOfAssessment["@value"]
    //   );
    //   const bCreated = new Date(
    //     b.FormResults.results.DateOfAssessment["@value"]
    //   );
    //   const aModified = new Date(a.ModifiedOn);
    //   const bModified = new Date(b.ModifiedOn);

    //   if (aCreated < bCreated) {
    //     return 1;
    //   }

    //   if (aCreated > bCreated) {
    //     return -1;
    //   }
    //   //passed current object may replace an object in allscreeners, but allscreeners is not updated
    //   //yet. So need to do secondary sort to bubble up the current object based on modified on by secondary sort
    //   if (aModified < bModified) {
    //     return 1;
    //   }
    //   if (aModified > bModified) {
    //     return -1;
    //   }
    //   return 0;
    // });

    // const mostRecentRiskLevel =
    //   all[0]?.FormResults?.results?.RiskLevel["@value"];
    // console.log("Risk:", mostRecentRiskLevel, all);

    // const toastId = toast.loading("Updating Contact...", {
    //   type: toast.TYPE.INFO,
    // });
    // updateEntity(
    //   "contact",
    //   clientData.contactid,
    //   JSON.stringify(obj),
    //   safyInfo
    // )
    //   .then(() => {
    //     toast.update(toastId, {
    //       render: "Contact has been updated.",
    //       type: toast.TYPE.SUCCESS,
    //       isLoading: false,
    //       autoClose: 5000,
    //       closeOnClick: true,
    //     });
    //     if (postSave) {
    //       postSave(clientData);
    //     }
    //   })
    //   .catch(() => {
    //     toast.update(toastId, {
    //       render: "An error occurred while updating the Contact.",
    //       type: toast.TYPE.ERROR,
    //       isLoading: false,
    //       autoClose: 5000,
    //       closeOnClick: true,
    //     });
    //   });
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(undefined);
  const [allScreeners, setAllScreeners] = useState();
  const [selectedScreenerDates, setSelectedScreenerDates] = useState([]);

  const allScreenerDatesUnique = useMemo(() => {
    if (allScreeners === undefined) {
      return [];
    }

    return allScreeners
      .map((screener) =>
        moment(screener.FormResults.results.DateOfAssessment["@value"]).format(
          "l"
        )
      )
      .filter((value, index, self) => self.indexOf(value) === index);
  }, [allScreeners]);

  const yLabels = [, "Low", "Moderate", "High"];

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "C-SSRS Screeners",
      },
    },
    scales: {
      y: {
        min: 0,
        max: 3.2,
        ticks: {
          stepSize: 1,
          callback: function (value) {
            return yLabels[value];
          },
        },
      },
    },
  };

  const chartData = useMemo(() => {
    if (allScreeners === undefined) {
      return;
    }

    const selectedScreeners = allScreeners.filter((screener) =>
      selectedScreenerDates.includes(
        moment(screener.FormResults.results.DateOfAssessment["@value"]).format(
          "l"
        )
      )
    );

    return {
      labels: [
        null,
        ...selectedScreeners.map((screener) =>
          moment(
            screener.FormResults.results.DateOfAssessment["@value"]
          ).format("l")
        ),
        null,
      ],
      datasets: [
        {
          label: "C-SSRS Screener Risk Levels",
          data: [
            null,
            ...selectedScreeners.map((screener) => {
              switch (getFieldValue(screener.FormResults.results.RiskLevel)) {
                case "High":
                  return 3;
                case "Moderate":
                  return 2;
                case "Low":
                  return 1;
                default:
                  return;
              }
            }),
            null,
          ],
          borderColor: "rgb(255, 99, 132)",
          backgroundColor: "rgba(255, 99, 132, 0.5)",
        },
      ],
    };
  }, [allScreeners, selectedScreenerDates]);

  useEffect(() => {
    if (!clientid) {
      return;
    }

    const loadScreeners = async () => {
      const response = await getFormsySubmissionsByFormId(
        "169172DF-8B88-ED11-AC20-0050F21C653C", // CSSRsScreener Form Id
        clientid
      );

      if (!isMountedRef.current) {
        return;
      }

      if (response === undefined || response === null) {
        setAllScreeners([]);
        return;
      }

      const results = response.filter((screener) => {
        if (screener.Active === "0") {
          return false;
        }

        const dateOfAssessment =
          screener.FormResults?.results?.DateOfAssessment?.["@value"];

        if (!dateOfAssessment) {
          return false;
        }

        return true;
      });

      const screeners = results.sort((a, b) => {
        const aDate = moment(a.FormResults.results.DateOfAssessment["@value"]);
        const bDate = moment(b.FormResults.results.DateOfAssessment["@value"]);

        if (aDate.isAfter(bDate)) {
          return 1;
        }

        if (aDate.isBefore(bDate)) {
          return -1;
        }

        return 0;
      });

      setAllScreeners(screeners);
      setSelectedScreenerDates(
        screeners
          .map((screener) =>
            moment(
              screener.FormResults.results.DateOfAssessment["@value"]
            ).format("l")
          )
          .filter((value, index, self) => self.indexOf(value) === index)
      );
    };

    loadScreeners();
  }, [clientid]);

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const handleRiskChange = ({ currentTarget: input }) => {
    const obj = handleChange({ currentTarget: input });

    if (obj.InProgress["@value"] === "Yes") {
      obj.RiskLevel["@value"] = "High";
    } else if (
      obj.HasWished["@value"] === "No" &&
      obj.HasSuicidalThoughts["@value"] === "No"
    ) {
      obj.RiskLevel["@value"] = "Low";
    } else if (
      obj.HasIntent["@value"] === "Yes" ||
      obj.HasDetails["@value"] === "Yes"
    ) {
      obj.RiskLevel["@value"] = "High";
    } else if (obj.HasPlans["@value"] === "Yes") {
      obj.RiskLevel["@value"] = "Moderate";
    } else {
      obj.RiskLevel["@value"] = "Low";
    }

    setData(obj);
  };

  const renderNextSteps = () => {
    if (
      data.HasWished["@value"] === undefined ||
      data.HasSuicidalThoughts["@value"] === undefined
    ) {
      return <></>;
    }

    const hasWished = getFieldValue(data.HasWished) === "Yes",
      hasSuicidalThoughts = getFieldValue(data.HasSuicidalThoughts) === "Yes";

    if (
      !hasWished &&
      !hasSuicidalThoughts &&
      data.InProgress["@value"] === undefined
    ) {
      return <></>;
    }

    if (
      (hasWished || hasSuicidalThoughts) &&
      (data.HasPlans["@value"] === undefined ||
        data.HasIntent["@value"] === undefined ||
        data.HasDetails["@value"] === undefined)
    ) {
      return <></>;
    }

    const steps = (
      <>
        {data.RiskLevel["@value"] === "High" ? (
          <div className="row border-start border-bottom">
            <div className="col py-2 border-end">
              <ul>
                <li>Notify and engage caregiver immediately</li>
                <li>Imminent danger, dial 911</li>
                <li>
                  Send client to nearest Hospital/ED/Crisis Center for immediate
                  assessment/ evaluation
                </li>
                <li>
                  Notify supervisor and custodian (if different from caregiver)
                </li>
                <li>Maintain contact throughout further assessment</li>
              </ul>
            </div>
            <div className="col py-2 border-end">
              <ul>
                <li>Complete an IR</li>
                <li>Coordinate follow-up steps with Tx Team</li>
                <li>Follow your program's follow-up contact requirements</li>
                <li>Re-screen at every session</li>
                <li>Review the safety plan at each visit</li>
              </ul>
            </div>
          </div>
        ) : data.RiskLevel["@value"] === "Moderate" ? (
          <div className="row border-start border-bottom">
            <div className="col py-2 border-end">
              <ul>
                <li>Notify and engage caregiver immediately</li>
                <li>
                  Arrange for a more thorough assessment (i.e. TD, crisis
                  center)
                </li>
                <li>Notify supervisor</li>
                <li>
                  Notify Custodian immediately (if different from caregiver)
                </li>
                <li>Maintain contact throughout further assessment</li>
              </ul>
            </div>
            <div className="col py-2 border-end">
              <ul>
                <li>Complete an IR</li>
                <li>Coordinate follow-up steps with Treatment Team</li>
                <li>Follow your program's follow-up contact requirements</li>
                <li>Complete a safety plan if not already done</li>
                <li>Re-screen at every session</li>
                <li>Review the safety plan at each visit</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="row border-start border-bottom">
            <div className="col py-2 border-end">
              <ul>
                <li>Notify and engage caregiver</li>
                <li>
                  Develop a safety plan with client and caregiver (or
                  review/modify existing plan)
                </li>
                <li>Notify Custodian (if different from caregiver)</li>
              </ul>
            </div>
            <div className="col py-2 border-end">
              <ul>
                <li>Complete an IR</li>
                <li>If not in therapy, arrange an intake</li>
                <li>Re-screen at every session</li>
                <li>Review the safety plan at each visit</li>
              </ul>
            </div>
          </div>
        )}
      </>
    );

    const riskLevel = getFieldValue(data.RiskLevel);
    const riskLevelStyle = {};

    if (riskLevel === "High") {
      riskLevelStyle.color = "red";
    } else if (riskLevel === "Moderate") {
      riskLevelStyle.color = "orange";
    } else {
      riskLevelStyle.color = "yellow";
    }

    return (
      <>
        <div className="h4 mb-2">
          Risk Level: <span style={riskLevelStyle}>{riskLevel}</span>
        </div>
        <div className="h5">Recommended Next Steps:</div>
        <div className="row border-start border-top border-bottom">
          <div className="col py-2 border-end">Immediate Next Steps</div>
          <div className="col py-2 border-end">Follow-Up Actions</div>
        </div>
        {steps}
      </>
    );
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="h5">C-SSRS Screener Questions</div>
              <div className="d-print-none mb-2">
                <i>
                  To be used, in the moment, after a client discloses suicidal
                  thoughts/behaviors to determine best next steps.
                </i>
              </div>
              <FormInput
                name="DateOfAssessment"
                label="Date of Assessment"
                error={errors.DateOfAssessment}
                value={data.DateOfAssessment["@value"]}
                onChange={handleChange}
                identical={compare("DateOfAssessment")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                type="date"
              />
              <FormRadioButton
                name="HasWished"
                label={
                  <b>
                    Have you wished you were dead or wished you could go to
                    sleep and not wake up?
                  </b>
                }
                error={errors.HasWished}
                value={getFieldValue(data.HasWished)}
                options={yesNoOptions}
                onChange={handleRiskChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormRadioButton
                name="HasSuicidalThoughts"
                label={
                  <b>Have you actually had any thoughts of killing yourself?</b>
                }
                error={errors.HasSuicidalThoughts}
                value={getFieldValue(data.HasSuicidalThoughts)}
                options={yesNoOptions}
                onChange={handleRiskChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.HasWished) === "Yes" ||
              getFieldValue(data.HasSuicidalThoughts) === "Yes" ? (
                <>
                  <FormRadioButton
                    name="HasPlans"
                    label={
                      <>
                        <b>
                          Have you been thinking about how you might do this?
                        </b>
                        <br />
                        <i>
                          Example: "I thought about taking an overdose, but I
                          never made a specific plan as to when, where, or how I
                          would actually do it … and I would never go through
                          with it."
                        </i>
                      </>
                    }
                    error={errors.HasPlans}
                    value={getFieldValue(data.HasPlans)}
                    options={yesNoOptions}
                    onChange={handleRiskChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <FormRadioButton
                    name="HasIntent"
                    label={
                      <>
                        <b>
                          Have you had these thoughts and had some intention of
                          acting on them?
                        </b>
                        <br />
                        <i>
                          As opposed to "I have these thoughts, but I definitely
                          won't do anything about them."
                        </i>
                      </>
                    }
                    error={errors.HasIntent}
                    value={getFieldValue(data.HasIntent)}
                    options={yesNoOptions}
                    onChange={handleRiskChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <FormRadioButton
                    name="HasDetails"
                    label={
                      <b>
                        Have you started to work out or worked out the details
                        of how to kill yourself? Did you intend to carry out
                        this plan?
                      </b>
                    }
                    error={errors.HasDetails}
                    value={getFieldValue(data.HasDetails)}
                    options={yesNoOptions}
                    onChange={handleRiskChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              <FormRadioButton
                name="InProgress"
                label={
                  <>
                    <b>
                      Have you done anything, started to do anything, or
                      prepared to do anything to end your life?
                    </b>
                    <br />
                    <i>
                      Examples: Took pills, tried to shoot yourself, cut
                      yourself, or hang yourself, took out pills but didn't
                      swallow any, held a gun but changed your mind or it was
                      grabbed from your hand, went to the roof but didn't jump,
                      collected pills, obtained a gun, gave away valuables,
                      wrote a will or suicide note, etc.
                    </i>
                  </>
                }
                error={errors.InProgress}
                value={getFieldValue(data.InProgress)}
                options={yesNoOptions}
                onChange={handleRiskChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {renderNextSteps()}
              <ExpandDiv
                id="Report"
                expandStatus={expandStatus}
                className="mt-2"
                title="C-SSRS Screener Report"
              >
                <Multiselect
                  data={allScreenerDatesUnique}
                  value={selectedScreenerDates}
                  onChange={setSelectedScreenerDates}
                />
                {selectedScreenerDates.length > 0 ? (
                  <LineGraph options={options} data={chartData} />
                ) : null}
              </ExpandDiv>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default CSSRsScreener;
